// composeBadge.js — client-side canvas composition for the personal shareable badge.
//
// Geometry is a FIXED convention shared with the Machina Content > Visuals designer
// template (static/badge/badge-frame-template.svg): a 1080x1080 canvas, the photo
// circle at (540,430) r300, and the name/team/event text band at the bottom. Admin
// frames must be built to this grid. Reuses the canvas idiom from CropImageHelper.js
// (createElement('canvas') -> drawImage -> toBlob('image/png')).
//
// All source images arrive as base64 data: URIs (from /api/my-badge), so they are
// same-origin to the canvas — canvas.toBlob() never hits a tainted-canvas error.
//
// The drawing helpers below are exported and shared with composeBadgeWide.js (the
// landscape Bravos member badge) — same engine, different grid and different message.

const SIZE = 1080;
const PHOTO_CX = 540;
const PHOTO_CY = 430;
const PHOTO_R = 300;
const NAME_Y = 820;
const TEAM_Y = 885;
const EVENT_Y = 960;
const TEXT_MAXW = 900;
export const FONT = "'Poppins', 'Segoe UI', Roboto, Arial, sans-serif";

export const loadImage = (src) =>
  new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null); // optional images degrade to null
    img.src = src; // data: URI => same-origin, no crossOrigin needed
  });

// Cover-fit an image into a circle and clip to it.
export const drawPhotoInCircle = (ctx, img, cx, cy, r) => {
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  const d = r * 2;
  const scale = Math.max(d / img.width, d / img.height);
  const w = img.width * scale;
  const h = img.height * scale;
  ctx.drawImage(img, cx - w / 2, cy - h / 2, w, h);
  ctx.restore();
};

// Contain-fit an image inside a box (no crop), centered.
export const drawContain = (ctx, img, bx, by, bw, bh) => {
  const scale = Math.min(bw / img.width, bh / img.height);
  const w = img.width * scale;
  const h = img.height * scale;
  ctx.drawImage(img, bx + (bw - w) / 2, by + (bh - h) / 2, w, h);
};

export const roundRect = (ctx, x, y, w, h, r) => {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
};

// Shrink the font until the text fits maxWidth, then ellipsize. Leaves ctx.font set.
export const fitText = (ctx, text, baseSize, weight, maxWidth) => {
  let size = baseSize;
  ctx.font = `${weight} ${size}px ${FONT}`;
  while (size > 20 && ctx.measureText(text).width > maxWidth) {
    size -= 2;
    ctx.font = `${weight} ${size}px ${FONT}`;
  }
  let t = text;
  if (ctx.measureText(t).width > maxWidth) {
    while (t.length > 1 && ctx.measureText(t + '…').width > maxWidth) t = t.slice(0, -1);
    t = t + '…';
  }
  return t;
};

export const luminance = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return 0;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
};

export const shade = (hex, amt) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const clamp = (v) => Math.max(0, Math.min(255, v));
  const r = clamp(((n >> 16) & 255) + amt);
  const g = clamp(((n >> 8) & 255) + amt);
  const b = clamp((n & 255) + amt);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

// Branded frame drawn entirely in code when no admin frame is uploaded.
const drawFallbackFrame = (ctx, { logo, primaryColor, secondaryColor }) => {
  const grad = ctx.createLinearGradient(0, 0, 0, SIZE);
  grad.addColorStop(0, shade(primaryColor, 24));
  grad.addColorStop(1, shade(primaryColor, -40));
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, SIZE, SIZE);

  if (logo) drawContain(ctx, logo, 340, 70, 400, 150);

  // ring around the photo circle
  ctx.save();
  ctx.lineWidth = 16;
  ctx.strokeStyle = secondaryColor || '#ffffff';
  ctx.beginPath();
  ctx.arc(PHOTO_CX, PHOTO_CY, PHOTO_R + 8, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
};

export const composeBadge = async ({
  photoDataUri, frameDataUri, logoDataUri,
  fullName, teamName, eventName, eventDate,
  primaryColor = '#0d1b2a', secondaryColor = '#e83e8c',
  lang,
}) => {
  const [photo, frame, logo] = await Promise.all([
    loadImage(photoDataUri), loadImage(frameDataUri), loadImage(logoDataUri),
  ]);
  if (!photo) throw new Error('noPhoto');

  try { if (document.fonts && document.fonts.ready) await document.fonts.ready; } catch (e) { /* fonts optional */ }

  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');

  if (frame) {
    ctx.fillStyle = shade(primaryColor, -20); // backdrop behind transparent frame areas
    ctx.fillRect(0, 0, SIZE, SIZE);
    drawPhotoInCircle(ctx, photo, PHOTO_CX, PHOTO_CY, PHOTO_R);
    ctx.drawImage(frame, 0, 0, SIZE, SIZE);
  } else {
    drawFallbackFrame(ctx, { logo, primaryColor, secondaryColor });
    drawPhotoInCircle(ctx, photo, PHOTO_CX, PHOTO_CY, PHOTO_R);
    // redraw the ring on top so the photo doesn't overlap it
    ctx.save();
    ctx.lineWidth = 16;
    ctx.strokeStyle = secondaryColor || '#ffffff';
    ctx.beginPath();
    ctx.arc(PHOTO_CX, PHOTO_CY, PHOTO_R + 8, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  // Legibility scrim behind the text band (subtle; works on any frame/background).
  ctx.save();
  const scrim = ctx.createLinearGradient(0, 740, 0, SIZE);
  scrim.addColorStop(0, 'rgba(0,0,0,0)');
  scrim.addColorStop(1, 'rgba(0,0,0,0.55)');
  ctx.fillStyle = scrim;
  ctx.fillRect(0, 740, SIZE, SIZE - 740);
  ctx.restore();

  // Text (always white on the scrim, with a soft shadow).
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#ffffff';
  ctx.shadowColor = 'rgba(0,0,0,0.45)';
  ctx.shadowBlur = 8;

  if (fullName) {
    const t = fitText(ctx, fullName, 64, 700, TEXT_MAXW);
    ctx.fillText(t, PHOTO_CX, NAME_Y);
  }
  if (teamName) {
    const t = fitText(ctx, teamName, 40, 600, TEXT_MAXW);
    ctx.fillStyle = '#ffffff';
    ctx.fillText(t, PHOTO_CX, TEAM_Y);
  }
  const dateStr = eventDate
    ? (() => { try { return new Date(eventDate).toLocaleDateString(lang || undefined, { day: 'numeric', month: 'long', year: 'numeric' }); } catch (e) { return ''; } })()
    : '';
  const eventLine = [eventName, dateStr].filter(Boolean).join('  •  ');
  if (eventLine) {
    const t = fitText(ctx, eventLine, 34, 500, TEXT_MAXW);
    ctx.fillText(t, PHOTO_CX, EVENT_Y);
  }
  ctx.restore();

  return await new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('toBlobFailed'))), 'image/png');
  });
};
